import { parse, parseISO, formatISO } from 'date-fns';

/**
 * The maximum file size for attachments, in bytes.
 * 10 * 1024 * 1024, 10 MB
 */
const maxAttachmentFileSize = 10485760;

function brand({
  validate = () => true
} = {}) {
  function assertIsBrand(value) {
    const result = validate(value);
    if (typeof result === 'string') {
      throw new Error(result);
    }
    if (result === false) {
      throw new Error(`Invalid value ${value}`);
    }
  }
  return {
    check: value => validate(value) === true,
    assert: assertIsBrand,
    from: value => {
      assertIsBrand(value);
      return value;
    }
  };
}

const DEFAULT_OPTIONS = {
  ISO8601DateExtended: false
};
/**
 * Converts the values of an string to a `Date`
 *
 * @param date the string you want converted to a `Date`
 * @param options the options for the conversion
 * @example
 * // If you want to parse only the date part of the string
 * // you can use the ISO8601DateExtended option
 * // legacy example: moment(date.substring(0, 10))
 *
 * const options = { ISO8601DateExtended: true };
 * getDateFromString('2021-01-01T00:00:00.000Z', options); // returns: Date
 */
const getDateFromString = (date, options = DEFAULT_OPTIONS) => {
  const {
    ISO8601DateExtended
  } = options;
  if (ISO8601DateExtended) {
    parse(date.substring(0, 10), 'yyyy-MM-dd', new Date());
  }
  return parseISO(date);
};
/**
 * Converts the values of a given `Date` to a date formatted string
 *
 * @param date the `Date` you want converted to a string
 * @param options the options for the conversion
 * @example
 * // If you want to convert only the date part of the Date
 * // you can use the ISO8601DateExtended option
 * // legacy example: moment(date.substring(0, 10))
 *
 * const date = new Date(); // '2021-01-01T00:00:00.000Z'
 * const options = { ISO8601DateExtended: true };
 * getStringFromDate(date, options); // returns: '2021-01-01'
 */
const getStringFromDate = (date, options = DEFAULT_OPTIONS) => {
  const {
    ISO8601DateExtended
  } = options;
  /**
   *  DEFAULT Date-fns options
   *  @see https://date-fns.org/v2.30.0/docs/formatISO#arguments
   */
  const formatOptions = {
    representation: 'complete',
    format: 'extended'
  };
  if (ISO8601DateExtended) {
    formatOptions.representation = 'date';
  }
  return formatISO(date, formatOptions);
};

/**
 * Converts the values of an enum to a human-readable array
 * @param enumObject the enum you want converted to an array of strings
 * @example
 * enum TestStatus {
 *     PENDING,
 *     OK,
 *     FAILED
 * }
 * console.log(...enumToArray(TestStatus)); // prints: "PENDING"  "OK"  "FAILED"
 * @example
 * enum TestStatus {
 *     PENDING = "pending",
 *     OK = "ok",
 *     FAILED = "failed"
 * }
 * console.log(...enumToArray(TestStatus)); // prints: "pending"  "ok"  "failed"
 */
function enumToArray(enumObject) {
  return Object.values(enumObject).filter(k => typeof k === 'string');
}

var ForceFullSyncObject;
(function (ForceFullSyncObject) {
  ForceFullSyncObject[ForceFullSyncObject["APPROVAL"] = 0] = "APPROVAL";
  ForceFullSyncObject[ForceFullSyncObject["CLASS"] = 1] = "CLASS";
  ForceFullSyncObject[ForceFullSyncObject["CONTRACT"] = 2] = "CONTRACT";
  ForceFullSyncObject[ForceFullSyncObject["COSTTYPE"] = 3] = "COSTTYPE";
  ForceFullSyncObject[ForceFullSyncObject["CUSTOMER"] = 4] = "CUSTOMER";
  ForceFullSyncObject[ForceFullSyncObject["DEPARTMENT"] = 5] = "DEPARTMENT";
  ForceFullSyncObject[ForceFullSyncObject["ITEM"] = 6] = "ITEM";
  ForceFullSyncObject[ForceFullSyncObject["LOCATION"] = 7] = "LOCATION";
  ForceFullSyncObject[ForceFullSyncObject["PROJECT"] = 8] = "PROJECT";
  ForceFullSyncObject[ForceFullSyncObject["PROJECTRESOURCE"] = 9] = "PROJECTRESOURCE";
  ForceFullSyncObject[ForceFullSyncObject["PROJECTSTATUS"] = 10] = "PROJECTSTATUS";
  ForceFullSyncObject[ForceFullSyncObject["TASK"] = 11] = "TASK";
  ForceFullSyncObject[ForceFullSyncObject["TASKRESOURCE"] = 12] = "TASKRESOURCE";
  ForceFullSyncObject[ForceFullSyncObject["TIMESHEET"] = 13] = "TIMESHEET";
  ForceFullSyncObject[ForceFullSyncObject["TIMESHEETENTRY"] = 14] = "TIMESHEETENTRY";
  ForceFullSyncObject[ForceFullSyncObject["TIMETYPE"] = 15] = "TIMETYPE";
  ForceFullSyncObject[ForceFullSyncObject["VENDOR"] = 16] = "VENDOR";
  ForceFullSyncObject[ForceFullSyncObject["WAREHOUSE"] = 17] = "WAREHOUSE";
})(ForceFullSyncObject || (ForceFullSyncObject = {}));

const AVAILABLE_ICONS = new Set(['acrobat', 'atom', 'chrome', 'evernote', 'explorer', 'finder', 'firefox', 'generic-app', 'generic-email', 'generic', 'gmail', 'google-calendar', 'google-docs', 'google-email', 'google-sheets', 'google-slides', 'harvest', 'illustrator', 'internet-explorer', 'iterm', 'itunes', 'mac-mail', 'magic-wand-active', 'magic-wand', 'microsoft-calendar', 'microsoft-email', 'microsoft-excel', 'microsoft-outlook', 'microsoft-powerpoint', 'microsoft-word', 'msedge', 'nabs', 'notes', 'notion', 'photoshop', 'premiere-pro', 'ringcentral', 'safari', 'sketch', 'slack', 'solidworks', 'spotify', 'teams', 'visual-studio-code', 'zoom']);
const SPECIAL_CASES = new Map([['acrord32', 'acrobat'], ['acrobat-reader', 'acrobat'], ['code', 'visual-studio-code'], ['google-chrome', 'chrome'], ['ms-teams', 'teams'], ['outlook', 'microsoft-outlook'], ['powerpnt', 'microsoft-powerpoint'], ['winword', 'microsoft-word'], ['excel', 'microsoft-excel'], ['notepad', 'notes'], ['textedit', 'notes'], ['microsoft-teams-(work-or-school', 'teams'], ['microsoft-teams-(work-or-school)', 'teams'], ['microsoft-teams', 'teams'], ['microsoft-teams-classic', 'teams']]);
const getIconName = input => {
  var _SPECIAL_CASES$get;
  if (!input) return 'generic.png';
  const cleanInput = input.toLowerCase().replace(/\.png$/, '').replace(/\.exe$/, '').replaceAll(/\s/g, '-');
  const finalName = (_SPECIAL_CASES$get = SPECIAL_CASES.get(cleanInput)) != null ? _SPECIAL_CASES$get : cleanInput;
  return AVAILABLE_ICONS.has(finalName) ? `${finalName}.png` : 'generic.png';
};

const TenantTypes = {
  customer_production: 'customer_production',
  partner_production: 'partner_production',
  /** https://jira.sage.com/browse/SIT-1446 */
  SIAP_production: 'SIAP_production',
  /** https://jira.sage.com/browse/SIT-1446 */
  MEDC_production: 'MEDC_production',
  sandbox: 'sandbox',
  developer: 'developer',
  sales_demo: 'sales_demo',
  implementation: 'implementation',
  debug: 'debug',
  test: 'test',
  preview: 'preview',
  other: 'other'
};
const PRODUCTION_TENANT_TYPES = [TenantTypes.customer_production, TenantTypes.partner_production, TenantTypes.MEDC_production, TenantTypes.SIAP_production];

/**
 * ```php
 * "production",
 * "implementation",
 * "sandbox",
 * "debug",
 * "demo",
 * "sample",
 * "trial",
 * "developer",
 * "salesdemo",
 * "test",
 * "template",
 * "preview",
 * "archive",
 * "training",
 * "NULL",
 * ```
 * @see https://github.com/intacct/ia-app/blob/03151e838529e8d856dc43cbdbd9b668cd81b3da/app/tools/eng/EditCompany.cls#L212
 */
const IntacctTenantTypes = {
  archive: 'archive',
  debug: 'debug',
  demo: 'demo',
  developer: 'developer',
  implementation: 'implementation',
  preview: 'preview',
  production: 'production',
  salesdemo: 'salesdemo',
  sample: 'sample',
  sandbox: 'sandbox',
  template: 'template',
  test: 'test',
  training: 'training',
  trial: 'trial'
};
const PRODUCTION_INTACCT_TENANT_TYPES = [IntacctTenantTypes.production];
function intacctTenantTypeToSITTenantType(intacctTenantType) {
  switch (intacctTenantType) {
    case IntacctTenantTypes.production:
      return TenantTypes.customer_production;
    case IntacctTenantTypes.demo:
      return TenantTypes.sales_demo;
    case IntacctTenantTypes.developer:
      return TenantTypes.developer;
    case IntacctTenantTypes.salesdemo:
      return TenantTypes.sales_demo;
    case IntacctTenantTypes.sandbox:
      return TenantTypes.sandbox;
    case IntacctTenantTypes.implementation:
      return TenantTypes.implementation;
    case IntacctTenantTypes.preview:
      return TenantTypes.preview;
    case IntacctTenantTypes.debug:
      return TenantTypes.debug;
    case IntacctTenantTypes.test:
      return TenantTypes.test;
    case IntacctTenantTypes.archive:
    case IntacctTenantTypes.sample:
    case IntacctTenantTypes.template:
    case IntacctTenantTypes.training:
      return TenantTypes.other;
    default:
      return TenantTypes.other;
  }
}

const CompanyIdHeader = 'X-SIT-Company-Id';
/** Used by the backend only, Intacct uses this header to sign messages to SIT */
const IntacctSignatureHeader = 'x-intacct-signature';

const SPECIAL_USERS = {
  intacct: {
    userId: 'intacct',
    email: 'noreply@intacct.com',
    role: 'admin',
    firstName: 'Support',
    lastName: 'User'
  },
  CPAUser: {
    userId: 'CPAUser',
    firstName: 'External',
    lastName: 'User'
    // Usually, these are `role: 'admin'`.
    // The email is a particular user's @intacct.com email.
  }
};
function isExternalUser(userId) {
  return userId.startsWith('ExtUser|');
}

// https://stackoverflow.com/a/22023833/4400318
function fileSize(bytes, decimals = 2) {
  const exp = Math.log(bytes) / Math.log(1024) | 0;
  const result = (bytes / 1024 ** exp).toFixed(exp === 0 ? 0 : decimals);
  return exp === 0 ? `${result} bytes` : `${result} ${'KMGTPEZY'[exp - 1]}B`; // \u200b could be used but it goes against Intacct's style guide
}

/**
 * @param xmlData
 * @returns the environment that the trigger package is for (the `<DisplayName>` value)
 * @throws if the xmlData is empty, or if the `<DisplayName>` value is empty
 */
function validateTriggerApplication(xmlData) {
  if (!xmlData) {
    throw new Error('Trigger package application is required');
  }
  // Get the contents between <DisplayName></DisplayName>
  const displayName = xmlData.match(/<DisplayName>(.*?)<\/DisplayName>/);
  if (!displayName) {
    throw new Error('DisplayName is required');
  }
  const displayNameValue = displayName[1];
  if (!displayNameValue) {
    throw new Error('DisplayName value is required');
  }
  return displayNameValue;
}

export { CompanyIdHeader, ForceFullSyncObject, IntacctSignatureHeader, IntacctTenantTypes, PRODUCTION_INTACCT_TENANT_TYPES, PRODUCTION_TENANT_TYPES, SPECIAL_USERS, TenantTypes, brand, enumToArray, fileSize, getDateFromString, getIconName, getStringFromDate, intacctTenantTypeToSITTenantType, isExternalUser, maxAttachmentFileSize, validateTriggerApplication };
//# sourceMappingURL=index.modern.js.map
